import api from './client';

// Simple in-flight de-duplication to avoid double POSTs from React Strict Mode
let lastKey: string | null = null;
let lastPromise: Promise<any> | null = null;

export type PartnerFilter = {
  partnerFullName?: string;
  partnerName?: string;
  country?: string;
  status?: string;
};

export type GetPartnersRequest = {
  filter: PartnerFilter;
  userId: string | null;
  role: string | null;
};

export async function getPartners(filter: PartnerFilter = {}) {
  // Pull userId and role from sessionStorage.workspaceData (NOT userData)
  let userId: string | null = null;
  let role: string | null = null;
  try {
    const raw = sessionStorage.getItem('workspaceData');
    if (raw) {
      const wd = JSON.parse(raw);
      userId = wd?.userId != null ? String(wd.userId) : (wd?.authUserId != null ? String(wd.authUserId) : null);
      const roleCandidate = wd?.role ?? wd?.currentRole ?? wd?.userRole ?? wd?.Role ?? wd?.roleName ?? wd?.RoleName;
      role = roleCandidate != null ? String(roleCandidate) : null;
    }
  } catch (_) {}
  // Soft fallback to userData role if workspaceData didn't have it
  if (!role) {
    try {
      const uraw = sessionStorage.getItem('userData');
      if (uraw) {
        const u = JSON.parse(uraw);
        const r = u?.role ?? u?.userRole ?? (Array.isArray(u?.roles) ? u.roles[0] : null);
        if (r) role = String(r);
      }
    } catch (_) {}
  }
  // Also fallback userId from userData if still missing
  if (!userId) {
    try {
      const uraw = sessionStorage.getItem('userData');
      if (uraw) {
        const u = JSON.parse(uraw);
        const uid = u?.id ?? u?.authUserId ?? u?.userId ?? null;
        if (uid) userId = String(uid);
      }
    } catch (_) {}
  }
  // Final defaults to decouple from workspace while UI is being wired
  if (!role) role = 'Admin';
  if (!userId) {
    const fallback = (import.meta as any)?.env?.VITE_DEFAULT_USER_ID || 'cf72e023-8fec-11ed-8ae4-00e04cc818b2';
    userId = String(fallback);
    if (import.meta.env.DEV) {
      console.warn('[PartnersAPI] Using fallback userId (no workspace):', userId);
    }
  }

  // Use POST with JSON body as requested
  const fullName = (filter?.partnerFullName ?? '').toString().trim();
  const shortName = (filter?.partnerName ?? '').toString().trim() || (fullName ? fullName.replace(/\s+/g, '') : '');
  const country = (filter?.country ?? '').toString().trim();
  const status = (filter?.status ?? '').toString().trim();

  const filterBody: Record<string, any> = {};
  if (fullName) filterBody.partnerFullName = fullName;
  if (shortName) filterBody.partnerName = shortName;
  if (country) filterBody.country = country;
  if (status) filterBody.status = status;

  const body: any = {
    filter: filterBody,
    userId: userId ? String(userId) : '',
    role: role ? String(role) : '',
  };

  // De-duplicate identical in-flight requests (particularly due to Strict Mode)
  const key = JSON.stringify(body);
  if (lastKey === key && lastPromise) {
    return lastPromise;
  }

  try {
    lastKey = key;
    if (import.meta.env.DEV) {
      console.debug('[PartnersAPI] POST /v1/partner/GetPartners', body);
    }
    lastPromise = api.post('/v1/partner/GetPartners', body).then(r => r.data).finally(() => {
      // Reset after a brief timeout to allow quick successive distinct queries later
      setTimeout(() => { lastKey = null; lastPromise = null; }, 50);
    });
    const data = await lastPromise;
    return data;
  } catch (err: any) {
    const status = err?.response?.status;
    // Retry once with PascalCase keys if the backend binder is strict
    if (status === 400) {
      const altBody: any = {
        Filter: {
          ...(filterBody.partnerFullName ? { PartnerFullName: filterBody.partnerFullName } : {}),
          ...(filterBody.partnerName ? { PartnerName: filterBody.partnerName } : {}),
          ...(filterBody.country ? { Country: filterBody.country } : {}),
          ...(filterBody.status ? { Status: filterBody.status } : {}),
        },
        UserId: userId ? String(userId) : '',
        Role: role ? String(role) : '',
      };
      // De-dup check for the PascalCase body as well
      const altKey = JSON.stringify(altBody);
      if (lastKey === altKey && lastPromise) return lastPromise;
      lastKey = altKey;
      if (import.meta.env.DEV) {
        console.debug('[PartnersAPI] RETRY PascalCase POST /v1/partner/GetPartners', altBody);
      }
      lastPromise = api.post('/v1/partner/GetPartners', altBody).then(r => r.data).finally(() => {
        setTimeout(() => { lastKey = null; lastPromise = null; }, 50);
      });
      const data = await lastPromise;
      return data;
    }
    throw err;
  }
}

export default {
  getPartners,
};
